import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Question } from "../../types/surveys";
import {
  ADVANCED_FORM_QUESTIONS,
  RUSSIAN_QUESTIONS,
  QUESTION_TYPE_LABEL,
} from "../../pages/recruiting/interview-questions";

type QuestionsScoresProps = {
  open: boolean;
  language?: string;
  initialScores?: Record<string, Question["score"]>;
  onClose: () => void;
  onSave: (scores: Record<string, Question["score"]>) => void;
};

type ScoreValue = number | "disqualify" | undefined;

const toScoreValue = (raw: string): ScoreValue => {
  const v = raw.trim();
  if (!v) return undefined;
  const lower = v.toLowerCase();
  if (lower === "dq" || lower === "disqualify") return "disqualify";
  const num = Number(v);
  return Number.isFinite(num) ? (num as number) : undefined;
};

const fromScoreValue = (v: ScoreValue): string => {
  if (v === undefined) return "";
  if (v === "disqualify") return "DQ";
  return String(v);
};

const QuestionsScores: React.FC<QuestionsScoresProps> = ({
  open,
  language,
  initialScores,
  onClose,
  onSave,
}) => {
  const questions: Question[] = useMemo(() => {
    const list: Question[] = [];
    if (language === "ru") list.push(...RUSSIAN_QUESTIONS);
    list.push(...ADVANCED_FORM_QUESTIONS);
    return list.filter((q) => q.type !== "placeholder");
  }, [language]);

  const [scores, setScores] = useState<Record<string, Question["score"]>>({});
  const [inputValues, setInputValues] = useState<Record<string, Record<string, string>>>({});

  useEffect(() => {
    // Merge provided scores with defaults from questions
    const merged: Record<string, Question["score"]> = {};
    const mergedInputs: Record<string, Record<string, string>> = {};
    for (const q of questions) {
      const effective = (initialScores && initialScores[q.id]) || q.score || {};
      merged[q.id] = effective;
      mergedInputs[q.id] = Object.fromEntries(
        Object.entries(effective as Record<string, any>).map(([k, v]) => [k, fromScoreValue(v)])
      );
    }
    setScores(merged);
    setInputValues(mergedInputs);
  }, [open, questions, initialScores]);

  const updateScore = (questionId: string, key: string, value: ScoreValue) => {
    setScores((prev) => {
      const current = { ...(prev[questionId] || {}) } as Record<string, ScoreValue>;
      if (value === undefined) {
        delete current[key];
      } else {
        current[key] = value;
      }
      return { ...prev, [questionId]: current as Question["score"] };
    });
  };

  const handleSave = () => {
    onSave(scores);
    onClose();
  };

  const handleResetToDefaults = () => {
    const reset: Record<string, Question["score"]> = {};
    for (const q of questions) {
      reset[q.id] = q.score || {};
    }
    setScores(reset);
  };

  const renderNumericOrDQField = (
    question: Question,
    key: string,
    label: string,
    width: number = 3
  ) => {
    const qScore = scores[question.id] || {};
    const value = (qScore as Record<string, ScoreValue>)[key];
    const textValue =
      (inputValues[question.id] && inputValues[question.id][key]) ?? fromScoreValue(value);
    return (
      <Grid item xs={12} sm={6} md={width} key={`${question.id}-${key}`}>
        <TextField
          label={label}
          fullWidth
          value={textValue}
          onChange={(e) => {
            const newVal = e.target.value;
            setInputValues((prev) => ({
              ...prev,
              [question.id]: { ...(prev[question.id] || {}), [key]: newVal },
            }));
          }}
          onBlur={(e) => {
            const parsed = toScoreValue(e.target.value);
            updateScore(question.id, key, parsed);
            // normalize input text from parsed value
            setInputValues((prev) => ({
              ...prev,
              [question.id]: {
                ...(prev[question.id] || {}),
                [key]: fromScoreValue(parsed),
              },
            }));
          }}
          placeholder="Number (can be negative) or DQ"
          size="small"
        />
      </Grid>
    );
  };

  const renderQuestionEditor = (question: Question) => {
    switch (question.type) {
      case "text":
        // Show question, but do not offer controls for scores
        return <></>;
      case "radio_yes_no":
        return (
          <Grid container spacing={2}>
            {renderNumericOrDQField(question, "true", "Yes")}
            {renderNumericOrDQField(question, "false", "No")}
          </Grid>
        );
      case "select":
      case "toggle":
        return (
          <Grid container spacing={2}>
            {(question.options || []).map((opt) =>
              renderNumericOrDQField(question, opt.value, opt.label, 3)
            )}
          </Grid>
        );
      case "select_multiple":
        return (
          <Grid container spacing={2}>
            {(question.options || []).map((opt) =>
              renderNumericOrDQField(question, opt.id, opt.label, 3)
            )}
          </Grid>
        );
      default:
        // text/number/date/boolean/multiline/country... use special keys
        return (
          <Grid container spacing={2}>
            {renderNumericOrDQField(question, "_any_answer_", "Any answer")}
            {renderNumericOrDQField(question, "_no_answer_", "No answer")}
          </Grid>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Set Question Scores</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Enter a number for score or "DQ" to disqualify.
        </Typography>
        <Grid container spacing={2}>
          {questions.map((q) => (
            <Grid item xs={12} key={q.id}>
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {q.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {QUESTION_TYPE_LABEL[q.type]}
                </Typography>
              </Box>
              {renderQuestionEditor(q)}
              <Divider sx={{ mt: 2 }} />
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleResetToDefaults} color="inherit">
          Reset to defaults
        </Button>
        <Box flexGrow={1}></Box>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionsScores;
